import { useState, useEffect } from "react";
import { Link, type ErrorComponentProps } from "@tanstack/react-router";
import { Sentry, sentryEnabled } from "@/lib/sentry";
import {
  AlertTriangle,
  SearchX,
  ArrowLeft,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function PageBackground({ children }: { children: React.ReactNode }) {
  return (
    <div className="noise bg-background relative min-h-dvh">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 -top-40 size-80 rounded-full bg-rose-500/[0.04] blur-[80px]" />
        <div className="absolute -bottom-40 -right-40 size-80 rounded-full bg-red-500/[0.03] blur-[80px]" />
      </div>
      <div className="bg-grid pointer-events-none absolute inset-0 opacity-[0.1]" />
      <div className="relative flex min-h-dvh flex-col items-center justify-center p-4">
        <div className="absolute left-6 top-6 flex items-center gap-2.5">
          <img src="/BLDR.png" alt="BLDR" className="size-7 rounded-lg" />
          <span className="font-heading text-sm font-bold">BLDR</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export function GlobalNotFound() {
  return (
    <PageBackground>
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <span className="font-heading text-8xl font-black text-rose-500 dark:text-rose-400">
            404
          </span>
        </div>

        <Card>
          <CardHeader className="items-center text-center">
            <SearchX className="text-muted-foreground size-4" />
            <h1 className="font-heading mt-4 text-balance text-2xl font-bold">
              Page not found
            </h1>
            <p className="text-muted-foreground text-pretty text-sm">
              The page you're looking for doesn't exist or has been moved.
            </p>
          </CardHeader>
          <CardContent className="flex justify-center pb-8">
            <Button
              asChild
              className="bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg shadow-rose-500/20 hover:from-rose-600 hover:to-red-700"
            >
              <Link to="/dashboard">
                <ArrowLeft />
                Go to Dashboard
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </PageBackground>
  );
}

export function GlobalError({ error, reset }: ErrorComponentProps) {
  const [showStack, setShowStack] = useState(false);
  const isDev = import.meta.env.DEV;

  useEffect(() => {
    if (sentryEnabled && error instanceof Error) {
      Sentry.captureException(error);
    }
  }, [error]);
  const errorMessage =
    error instanceof Error ? error.message : "An unexpected error occurred";
  const stack = error instanceof Error ? error.stack : undefined;

  return (
    <PageBackground>
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader className="items-center text-center">
            <AlertTriangle className="text-muted-foreground size-4" />
            <h1 className="font-heading mt-4 text-balance text-2xl font-bold">
              Something went wrong
            </h1>
            <p className="text-muted-foreground text-pretty text-sm">
              {errorMessage}
            </p>
          </CardHeader>
          <CardContent className="space-y-4 pb-8">
            <div className="flex justify-center gap-3">
              <Button variant="outline" asChild>
                <Link to="/dashboard">
                  <ArrowLeft />
                  Dashboard
                </Link>
              </Button>
              <Button
                onClick={reset}
                className="bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg shadow-rose-500/20 hover:from-rose-600 hover:to-red-700"
              >
                <RefreshCw />
                Try Again
              </Button>
            </div>

            {isDev && stack && (
              <div className="pt-2">
                <button
                  onClick={() => setShowStack((s) => !s)}
                  className="text-muted-foreground hover:text-foreground flex w-full items-center gap-2 text-xs"
                >
                  <ChevronDown
                    className={`size-3 transition-transform ${showStack ? "rotate-180" : ""}`}
                  />
                  Stack trace
                </button>
                {showStack && (
                  <pre className="bg-muted/50 text-muted-foreground mt-2 max-h-64 overflow-auto rounded-lg border p-3 font-mono text-xs leading-relaxed">
                    {stack}
                  </pre>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageBackground>
  );
}
